/**
 * Translate a v1 search deep link into a v2 one.
 *
 * The Chrome extension links into v1's search page with a pre-filled query string
 * (`/app/account/tracking?state=CA&carrier=fedex&…`). v2's search page speaks a different
 * vocabulary and deliberately accepts none of v1's spellings — see the v2 repo's
 * `docs/search-deep-link.md`, which is the contract this file targets. This module owns the
 * mapping so the legacy vocabulary lives in one place and dies with v1, and so installed
 * extension builds keep working without an extension release.
 *
 * v1's own parsing is `views/TrackingNrs/TrackingNrsQuery.tsx:104-133`, and the "does this
 * filter actually apply" gating is at :225-232. Both are reproduced here, because the job is
 * to land the user on *the search v1 would have run*, not on a tidier one. The two places we
 * knowingly diverge are the ones where v1 silently discarded input:
 *
 *   - Matching is case-insensitive. v1 wanted `CA` or `california` and dropped `California`
 *     on the floor; a link that was quietly broken becomes one that works.
 *   - `origin` halves are trimmed. v1 split `"Los Angeles, CA"` and kept `" CA"`, which
 *     matched no state.
 *
 * Everything v1 ignored stays ignored, so a translated link cannot search for something v1
 * would not have: `check-walmart` has no v2 equivalent (and was a purchase option, not a
 * filter), and `allow-photo`/`allow-signature` are hardcoded to true in v1.
 */

import moment from 'moment';
import countries from 'src/views/TrackingNrs/countries';
import states from 'src/views/TrackingNrs/states';

/** v2's carrier set. v1 additionally offers `any`, which means all of them. */
const V2_CARRIERS = ['fedex', 'dhl', 'ups'];

/** v1 statuses (plus the ones v1 has commented out) -> v2's vocabulary. */
const V2_STATUS: Record<string, string> = {
  'in-transit': 'transit',
  transit: 'transit',
  delivered: 'delivered',
  'pre-transit': 'pre-transit',
};

// v1 fed these to `moment(v)`, which accepts nearly anything. Strict-parse the shapes worth
// honouring and drop the rest, so a typo falls back to v1's default instead of reaching v2 as
// an "Invalid date" it would only reject anyway.
const DATE_FORMATS = ['YYYY-MM-DD', 'YYYY/MM/DD', 'MM/DD/YYYY'];

/** Attribution params. v2 documents these as safely ignored, so they cost nothing to keep. */
const PASSTHROUGH = /^(utm_|gclid$|fbclid$|msclkid$)/;

const clean = (v: string | null): string => (v || '').trim();

/** v1's own truthiness test: `!!qp.get(name)`. Present-and-non-empty, value never inspected. */
const isFlagSet = (qp: URLSearchParams, name: string): boolean => !!qp.get(name);

function resolveCountry(raw: string): string | undefined {
  const v = raw.trim().toLowerCase();
  if (!v) return undefined;
  const hit = countries.find((c) => c.code.toLowerCase() === v)
    || countries.find((c) => c.name.toLowerCase() === v);
  return hit?.code;
}

function resolveState(raw: string): string | undefined {
  const v = raw.trim().toLowerCase();
  if (!v) return undefined;
  const hit = states.find((s) => s.abbreviation.toLowerCase() === v)
    || states.find((s) => s.name.toLowerCase() === v);
  return hit?.abbreviation;
}

function resolveDate(raw: string): string | undefined {
  const v = raw.trim();
  if (!v) return undefined;
  const m = moment(v, DATE_FORMATS, true);
  return m.isValid() ? m.format('YYYY-MM-DD') : undefined;
}

/**
 * v1 gates weights behind `enableMinMaxWeight && !!weight`, so a zero or a non-number applies
 * no filter at all. Same here — v2 would accept `0` as a real lower bound.
 */
function resolveWeight(raw: string): string | undefined {
  const v = raw.trim();
  if (!v) return undefined;
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? String(n) : undefined;
}

/**
 * Build the v2 query string for a v1 search URL. Takes and returns the query string only
 * (leading `?` optional on the way in, absent on the way out).
 *
 * `now` is injectable so the delivery-window defaults are testable.
 */
export function v2SearchQueryFromV1(search: string, now: Date = new Date()): string {
  const qp = new URLSearchParams(search.replace(/^\?/, ''));
  const out = new URLSearchParams();

  // --- carriers -------------------------------------------------------------------------
  // v1: `any` (its default) or one carrier. An unrecognised value gets dropped rather than
  // forwarded — v2 defaults to all three, which is what v1's `any` means anyway.
  const carrier = clean(qp.get('carrier')).toLowerCase();
  if (carrier === 'any') {
    out.set('carriers', V2_CARRIERS.join(','));
  } else if (V2_CARRIERS.indexOf(carrier) !== -1) {
    out.set('carriers', carrier);
  }

  // --- status ---------------------------------------------------------------------------
  // v1 is multi-select and comma-separated; v2 takes exactly one. Keep the first value that
  // maps, which is what v2 itself does with a stale multi-select preference blob.
  const status = clean(qp.get('status'))
    .split(',')
    .map((s) => V2_STATUS[s.trim().toLowerCase()])
    .find(Boolean);
  if (status) out.set('status', status);

  // --- destination ----------------------------------------------------------------------
  // v1 defaults to US and falls back to US for anything it cannot resolve. Always emitted:
  // v2 reads a link against its own base defaults, never the recipient's saved filters, so
  // every value we want has to be on the wire.
  const destCountry = resolveCountry(clean(qp.get('country'))) || 'US';
  out.set('dest_country', destCountry);

  // State is US-only on both sides (v1 sends `state` only when the destination is domestic).
  const destState = destCountry === 'US' ? resolveState(clean(qp.get('state'))) : undefined;
  if (destState) out.set('dest_state', destState);

  const destCity = clean(qp.get('city'));
  if (destCity) out.set('dest_city', destCity);

  // --- estimated delivery window --------------------------------------------------------
  // v2 requires both bounds or neither, and v1's defaults (+3d / +7d) differ from v2's
  // (today / +2d) — so an omitted bound is filled with what v1 would have used rather than
  // left to v2. For `status=delivered` this window is in the future and v2 snaps it back to
  // (today-7 … today-1); v1 snapped the same case to (today-3 … today-1). Near enough that
  // reproducing v1's arithmetic here would be false precision.
  const from = resolveDate(clean(qp.get('from')))
    || moment(now).add(3, 'days').format('YYYY-MM-DD');
  const to = resolveDate(clean(qp.get('to')))
    || moment(now).add(7, 'days').format('YYYY-MM-DD');
  out.set('est_delivery_from', from);
  out.set('est_delivery_to', to);

  // --- ship date window -----------------------------------------------------------------
  // In v1 the dates only apply when the section is open, and `ship-date`/`max-ship-date` are
  // the flags that open it — presence-only, value ignored. Without one, v1 shows the dates
  // and filters on neither, so neither crosses over.
  if (isFlagSet(qp, 'ship-date') || isFlagSet(qp, 'max-ship-date')) {
    const shippedFrom = resolveDate(clean(qp.get('min-shipped-at')))
      || moment(now).subtract(3, 'days').format('YYYY-MM-DD');
    const shippedTo = resolveDate(clean(qp.get('max-shipped-at')))
      || moment(now).format('YYYY-MM-DD');
    out.set('shipped_from', shippedFrom);
    out.set('shipped_to', shippedTo);
  }

  // --- weight ---------------------------------------------------------------------------
  // Pounds on both sides. v1 converts to grams for its own API; v2 does its own conversion.
  const minWeight = resolveWeight(clean(qp.get('min-weight')));
  const maxWeight = resolveWeight(clean(qp.get('max-weight')));
  if (minWeight) out.set('weight_min_lbs', minWeight);
  if (maxWeight) out.set('weight_max_lbs', maxWeight);

  // --- origin ---------------------------------------------------------------------------
  // `origin` is both the value and the switch: without it v1 applies no origin filter at all,
  // `origin-country` included. One field holding "City,ST"; v2 has the two split out.
  const origin = clean(qp.get('origin'));
  if (origin) {
    const [rawCity, rawState] = origin.split(',');
    const originCountry = resolveCountry(clean(qp.get('origin-country'))) || 'US';
    out.set('origin_country', originCountry);

    const originCity = clean(rawCity);
    if (originCity) out.set('origin_city', originCity);

    // US-only, same as the destination state.
    const originState = originCountry === 'US' ? resolveState(clean(rawState)) : undefined;
    if (originState) out.set('origin_state', originState);
  }

  // --- everything else ------------------------------------------------------------------
  // Attribution rides along; unknown filters do not. v2 ignores what it does not recognise,
  // but forwarding v1 spellings would only make a broken link look like a supported one.
  qp.forEach((value, key) => {
    if (PASSTHROUGH.test(key) && value) out.set(key, value);
  });

  return out.toString();
}

export default v2SearchQueryFromV1;
